import { useTranslation } from "react-i18next";
import { formatMonthName, monthChange } from "./usagePace";
import { comparisonArrow, comparisonSentence } from "./monthComparisonText";
import styles from "./MonthComparison.module.css";
import { Tooltip } from "../shared/Tooltip";

/** The previous period's total for one scope (the consolidated figure, or one vendor), as the history response gives it. */
export interface PreviousTotal {
  periodStart: string;
  total: number | null;
  currency: string | null;
}

/**
 * A small chip comparing this period's figure with the previous month's, for a vendor card: "↑ 12%
 * vs September". The chip itself is decorative; screen readers get the full sentence instead ("On
 * course for 12% more than September" on the current month, against the projected month end, or
 * "12% more than September" for a past month, against its actual total). Renders nothing when there
 * is no previous total, the previous total is zero, or the currencies differ.
 *
 * @param props.currentAmount - This period's figure: the projected month end, or the actual total for a past month.
 * @param props.currentCurrency - The currency of `currentAmount`.
 * @param props.previous - The previous month's total for the same scope, or null when there is no previous period.
 * @param props.basis - Whether `currentAmount` is a projection (current month) or an actual total (past month).
 */
export function MonthComparison({
  currentAmount,
  currentCurrency,
  previous,
  basis,
}: {
  currentAmount: number;
  currentCurrency: string;
  previous: PreviousTotal | null;
  basis: "projected" | "actual";
}): React.JSX.Element | null {
  const { t } = useTranslation();

  if (previous?.currency !== currentCurrency) {
    return null;
  }

  const change = monthChange(currentAmount, previous.total);
  if (!change) {
    return null;
  }

  const month = formatMonthName(previous.periodStart);
  const arrow = comparisonArrow(change);
  const chipText =
    change.direction === "same"
      ? t("consumption.comparison.chipSame", { month })
      : t("consumption.comparison.chip", { percent: change.percent, month });

  return (
    <p className={styles.chip}>
      <Tooltip label={comparisonSentence(t, change, basis, month)}>
        <span aria-hidden="true">
          {arrow && `${arrow} `}
          {chipText}
        </span>
        <span className="visually-hidden">{comparisonSentence(t, change, basis, month)}</span>
      </Tooltip>
    </p>
  );
}
